// Role-Based Access Control (RBAC) Configuration
// Professional enterprise permission system

export type Permission = 
  | 'read'
  | 'create'
  | 'edit'
  | 'delete'
  | 'schedule'
  | 'approve'
  | 'reject';

export type Role = 
  | 'ADMINISTRATOR'
  | 'ABM_MANAGER'
  | 'ABM_OPERATIONS'
  | 'MDA'
  | 'SALES';

export type Module = 
  | 'dashboard'
  | 'accounts'
  | 'contacts'
  | 'abm_journey'
  | 'call_management'
  | 'mql_gate'
  | 'magnets'
  | 'users'
  | 'prospecting'
  | 'reports';

// Role display configuration
export const ROLE_CONFIG = {
  ADMINISTRATOR: {
    label: 'Administrator',
    description: 'Full system access',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  },
  ABM_MANAGER: {
    label: 'ABM Manager',
    description: 'Manage ABM operations and approve MQL',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  ABM_OPERATIONS: {
    label: 'ABM Operations',
    description: 'Execute ABM activities and submit MQL',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  MDA: {
    label: 'MDA - Manufacturing Discovery Analyst',
    description: 'Discovery and analysis focused',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
  },
  SALES: {
    label: 'Sales',
    description: 'View-only access to MQL',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
  },
};

// Comprehensive permission matrix
export const PERMISSIONS: Record<Role, Record<Module, Permission[]>> = {
  // Administrator - Full access
  ADMINISTRATOR: {
    dashboard: ['read', 'create', 'edit', 'delete'],
    accounts: ['read', 'create', 'edit', 'delete'],
    contacts: ['read', 'create', 'edit', 'delete'],
    abm_journey: ['read', 'create', 'edit', 'delete'],
    call_management: ['read', 'create', 'edit', 'delete', 'schedule'],
    mql_gate: ['read', 'create', 'edit', 'delete', 'approve', 'reject'],
    magnets: ['read', 'create', 'edit', 'delete'],
    users: ['read', 'create', 'edit', 'delete'],
    prospecting: ['read', 'create', 'edit', 'delete', 'approve'],
    reports: ['read', 'create', 'edit', 'delete'],
  },

  // ABM Manager - Manage ABM, approve MQL
  ABM_MANAGER: {
    dashboard: ['read', 'edit'],
    accounts: ['read', 'create', 'edit'],
    contacts: ['read', 'create', 'edit'],
    abm_journey: ['read', 'create', 'edit'],
    call_management: ['read', 'create', 'edit', 'schedule'],
    mql_gate: ['read', 'create', 'edit', 'approve', 'reject'],
    magnets: ['read', 'create', 'edit'],
    users: ['read', 'create', 'edit'],
    prospecting: ['read', 'create', 'edit', 'approve'],
    reports: ['read', 'create', 'edit'],
  },

  // ABM Operations - Execute ABM, submit MQL
  ABM_OPERATIONS: {
    dashboard: ['read', 'edit'],
    accounts: ['read', 'create', 'edit'],
    contacts: ['read', 'create', 'edit'],
    abm_journey: ['read', 'create', 'edit'],
    call_management: ['read', 'create', 'edit', 'schedule'],
    mql_gate: ['read', 'create', 'edit'],
    magnets: ['read', 'create', 'edit'],
    users: ['read', 'create', 'edit'],
    prospecting: ['read', 'create', 'edit', 'approve'],
    reports: ['read'],
  },

  // MDA - Discovery focused
  MDA: {
    dashboard: ['read'],
    accounts: ['read'],
    contacts: ['read'],
    abm_journey: ['read'],
    call_management: ['read'],
    mql_gate: [],
    magnets: ['read'],
    users: [],
    prospecting: ['read', 'create', 'edit', 'approve'],
    reports: ['read'],
  },

  // Sales - View-only MQL
  SALES: {
    dashboard: ['read'],
    accounts: ['read'],
    contacts: ['read'],
    abm_journey: ['read'],
    call_management: [],
    mql_gate: ['read'],
    magnets: [],
    users: [],
    prospecting: [],
    reports: ['read'],
  },
};

// Helper functions
export const hasPermission = (
  userRole: Role,
  module: Module,
  permission: Permission
): boolean => {
  const rolePermissions = PERMISSIONS[userRole];
  if (!rolePermissions) return false;
  const modulePermissions = rolePermissions[module];
  if (!modulePermissions) return false;
  return modulePermissions.includes(permission);
};

export const canRead = (userRole: Role, module: Module): boolean => {
  return hasPermission(userRole, module, 'read');
};

export const canCreate = (userRole: Role, module: Module): boolean => {
  return hasPermission(userRole, module, 'create');
};

export const canEdit = (userRole: Role, module: Module): boolean => {
  return hasPermission(userRole, module, 'edit');
};

export const canDelete = (userRole: Role, module: Module): boolean => {
  return hasPermission(userRole, module, 'delete');
};

export const canSchedule = (userRole: Role, module: Module): boolean => {
  return hasPermission(userRole, module, 'schedule');
};

export const canApprove = (userRole: Role, module: Module): boolean => {
  return hasPermission(userRole, module, 'approve');
};

export const canReject = (userRole: Role, module: Module): boolean => {
  return hasPermission(userRole, module, 'reject');
};

export const getAccessibleModules = (userRole: Role): Module[] => {
  const modules: Module[] = [];
  const rolePermissions = PERMISSIONS[userRole];
  Object.entries(rolePermissions).forEach(([module, permissions]) => {
    if (permissions.includes('read')) {
      modules.push(module as Module);
    }
  });
  return modules;
};

export const hasAnyPermission = (userRole: Role, module: Module): boolean => {
  const rolePermissions = PERMISSIONS[userRole];
  if (!rolePermissions) return false;
  const modulePermissions = rolePermissions[module];
  return modulePermissions && modulePermissions.length > 0;
};

export const getModulePermissions = (
  userRole: Role,
  module: Module
): Permission[] => {
  const rolePermissions = PERMISSIONS[userRole];
  if (!rolePermissions) return [];
  return rolePermissions[module] || [];
};

export const getNavigationItems = (userRole: Role) => {
  const items = [];
  if (canRead(userRole, 'dashboard')) {
    items.push({ path: '/dashboard', label: 'Dashboard', module: 'dashboard' });
  }
  if (canRead(userRole, 'accounts')) {
    items.push({ path: '/accounts', label: 'Accounts', module: 'accounts' });
  }
  if (canRead(userRole, 'contacts')) {
    items.push({ path: '/contacts', label: 'Contacts', module: 'contacts' });
  }
  if (canRead(userRole, 'abm_journey')) {
    items.push({ path: '/abm-journeys', label: 'ABM Journey', module: 'abm_journey' });
  }
  if (canRead(userRole, 'call_management')) {
    items.push({ path: '/calls', label: 'Calls', module: 'call_management' });
  }
  if (canRead(userRole, 'mql_gate')) {
    items.push({ path: '/mql', label: 'MQL Gate', module: 'mql_gate' });
  }
  if (canRead(userRole, 'magnets')) {
    items.push({ path: '/magnets', label: 'Magnets', module: 'magnets' });
  }
  if (canRead(userRole, 'users')) {
    items.push({ path: '/users', label: 'Users', module: 'users' });
  }
  if (canRead(userRole, 'reports')) {
    items.push({ path: '/reports', label: 'Reports', module: 'reports' });
  }
  return items;
};

export default PERMISSIONS;
